<template>
  <div class="project-overview">
    <!-- 第一行：项目信息 + 项目进度 -->
    <div class="row">
      <!-- 项目信息卡片 -->
      <div class="card project-info-card">
        <div class="card-header">
          <i class="el-icon-folder"></i>
          <span>项目信息</span>
        </div>
        <div class="card-body">
          <div class="info-item">
            <label>项目名称</label>
            <span class="value">{{ project.name || project.Name || '-' }}</span>
          </div>
          <div class="info-item">
            <label>项目代号</label>
            <span class="value">{{ project.code || project.Code || '-' }}</span>
          </div>
          <div class="info-item">
            <label>项目时间</label>
            <span class="value">{{ projectTimeRange }}</span>
          </div>
          <div class="info-item">
            <label>项目状态</label>
            <el-tag :type="projectStatus === 1 ? 'success' : 'info'" size="small">
              {{ projectStatus === 1 ? '进行中' : '已完成' }}
            </el-tag>
          </div>
        </div>
      </div>

      <!-- 项目进度卡片 -->
      <div class="card project-progress-card">
        <div class="card-header">
          <i class="el-icon-time"></i>
          <span>项目进度</span>
        </div>
        <div class="card-body">
          <div class="progress-main">
            <div class="progress-percent">{{ riskData.completionRate }}%</div>
            <el-progress 
              :percentage="riskData.completionRate" 
              :stroke-width="12"
              :color="progressColor"
            ></el-progress>
          </div>
          <div class="time-info">
            <div class="time-item">
              <label>开始时间</label>
              <span>{{ projectStartTime }}</span>
            </div>
            <div class="time-item">
              <label>截止时间</label>
              <span>{{ projectEndTime }}</span>
            </div>
            <div class="time-item">
              <label>剩余天数</label>
              <span :class="{ 'text-danger': riskData.remainingDays <= 7 }">
                {{ riskData.remainingDays }} 天
              </span>
            </div>
          </div>
          <!-- 超期风险 -->
          <div class="risk-indicator" :class="'risk-' + riskData.overdueRisk">
            <i :class="riskIcon"></i>
            <span class="risk-label">{{ riskLabel }}</span>
            <span class="risk-value" v-if="riskData.overdueRate > 0">
              超期概率 {{ riskData.overdueRate }}%
            </span>
            <span class="risk-hint" v-if="riskData.needSpeedUp">
              需加速 {{ riskData.speedUpRatio }}x
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 第二行：配额完成度 -->
    <div class="row">
      <div class="card quota-card full-width">
        <div class="card-header">
          <i class="el-icon-data-analysis"></i>
          <span>配额完成度</span>
        </div>
        <div class="card-body">
          <div class="quota-summary">
            <div class="summary-item" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
              <div class="item-value">{{ quota.total }}</div>
              <div class="item-label">总配额</div>
            </div>
            <div class="summary-item" style="background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);">
              <div class="item-value">{{ quota.completed }}</div>
              <div class="item-label">已完成</div>
            </div>
            <div class="summary-item" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
              <div class="item-value">{{ quota.pending }}</div>
              <div class="item-label">待完成</div>
            </div>
            <div class="summary-item" style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);">
              <div class="item-value">{{ quota.rate }}%</div>
              <div class="item-label">完成率</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 第三行：成员概览 + 问卷回收进度 -->
    <div class="row">
      <!-- 成员概览卡片 -->
      <div class="card members-card">
        <div class="card-header">
          <i class="el-icon-user"></i>
          <span>项目成员</span>
          <span class="count-badge">{{ members.length }}人</span>
        </div>
        <div class="card-body">
          <div class="role-stats">
            <div class="role-item">
              <span class="role-count">{{ memberStats.admin }}</span>
              <span class="role-label">管理员</span>
            </div>
            <div class="role-item">
              <span class="role-count">{{ memberStats.member }}</span>
              <span class="role-label">成员</span>
            </div>
            <div class="role-item">
              <span class="role-count">{{ memberStats.viewer }}</span>
              <span class="role-label">查看者</span>
            </div>
          </div>
          <div class="member-avatars">
            <div 
              v-for="(m, i) in displayMembers" 
              :key="m.id || i" 
              class="avatar"
              :title="m.name || m.Name || m.username"
            >
              {{ getInitial(m.name || m.Name || m.username) }}
            </div>
            <div v-if="members.length > 5" class="avatar more">
              +{{ members.length - 5 }}
            </div>
          </div>
        </div>
      </div>

      <!-- 波次回收进度卡片 -->
      <div class="card forms-progress-card">
        <div class="card-header">
          <i class="el-icon-s-order"></i>
          <span>波次回收进度</span>
        </div>
        <div class="card-body">
          <div class="form-progress-list">
            <div v-for="wave in waves" :key="wave.id" class="form-item">
              <div class="wave-info">
                <div class="wave-name" :title="wave.name">
                  {{ wave.name }}
                </div>
                <el-tag v-if="wave.isOverdue" size="mini" type="danger">已超期</el-tag>
                <el-tag v-else-if="wave.remainingDays !== null && wave.remainingDays <= 7" size="mini" type="warning">
                  剩余{{ wave.remainingDays }}天
                </el-tag>
              </div>
              <div class="form-progress">
                <el-progress 
                  :percentage="wave.percentage" 
                  :stroke-width="8"
                  :color="wave.isOverdue ? '#f56c6c' : (wave.percentage >= 100 ? '#67c23a' : '#409eff')"
                ></el-progress>
              </div>
              <div class="form-stats">
                {{ wave.completedCount || 0 }} / {{ wave.dealerTotal || 0 }}
              </div>
            </div>
            <div v-if="waves.length === 0" class="empty-tip">
              暂无波次
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 第四行：数据审核进度（只显示有审核数据的波次） -->
    <div class="row" v-if="reviewWaves.length > 0">
      <div class="card review-progress-card full-width">
        <div class="card-header">
          <i class="el-icon-finished"></i>
          <span>数据审核进度</span>
        </div>
        <div class="card-body">
          <div class="review-waves-list">
            <div v-for="wave in reviewWaves" :key="'review-' + wave.id" class="review-wave-block">
              <div class="wave-header">
                <span class="wave-name">{{ wave.name }}</span>
                <span class="wave-total">共 {{ wave.reviewTotal || 0 }} 份答卷</span>
              </div>
              <div class="review-items">
                <!-- 待审核 -->
                <div class="review-item">
                  <div class="review-label">待审核</div>
                  <div class="review-progress">
                    <el-progress 
                      :percentage="wave.reviewTotal > 0 ? Math.round(wave.reviewPending / wave.reviewTotal * 100) : 0" 
                      :stroke-width="8"
                      color="#909399"
                    ></el-progress>
                  </div>
                  <div class="review-stats">{{ wave.reviewPending || 0 }} / {{ wave.reviewTotal || 0 }}</div>
                </div>
                <!-- 一审 -->
                <div class="review-item">
                  <div class="review-label">一审</div>
                  <div class="review-progress">
                    <el-progress 
                      :percentage="wave.reviewTotal > 0 ? Math.round(wave.review1 / wave.reviewTotal * 100) : 0" 
                      :stroke-width="8"
                      color="#e6a23c"
                    ></el-progress>
                  </div>
                  <div class="review-stats">{{ wave.review1 || 0 }} / {{ wave.reviewTotal || 0 }}</div>
                </div>
                <!-- 二审 -->
                <div class="review-item">
                  <div class="review-label">二审</div>
                  <div class="review-progress">
                    <el-progress 
                      :percentage="wave.reviewTotal > 0 ? Math.round(wave.review2 / wave.reviewTotal * 100) : 0" 
                      :stroke-width="8"
                      color="#409eff"
                    ></el-progress>
                  </div>
                  <div class="review-stats">{{ wave.review2 || 0 }} / {{ wave.reviewTotal || 0 }}</div>
                </div>
                <!-- 三审 -->
                <div class="review-item">
                  <div class="review-label">三审</div>
                  <div class="review-progress">
                    <el-progress 
                      :percentage="wave.reviewTotal > 0 ? Math.round(wave.review3 / wave.reviewTotal * 100) : 0" 
                      :stroke-width="8"
                      color="#67c23a"
                    ></el-progress>
                  </div>
                  <div class="review-stats">{{ wave.review3 || 0 }} / {{ wave.reviewTotal || 0 }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProjectDetail, getProjectQuota, getProjectMembers } from 'index/api/project/manager'
import { pageFormRequest } from 'index/api/project/form'
import { getWaveList, getWaveDealers } from 'index/api/project/wave'

export default {
  name: 'ProjectOverview',

  data() {
    return {
      loading: false,
      project: {},
      quota: {
        total: 0,
        completed: 0,
        pending: 0,
        rate: 0
      },
      quotaList: [],
      members: [],
      forms: [],
      waves: []  // 波次数据
    }
  },

  computed: {
    projectId() {
      return this.$route.params.projectId
    },

    // 项目状态（兼容不同命名格式）
    projectStatus() {
      return this.project.status ?? this.project.Status ?? 0
    },

    // 项目时间范围（显示格式）
    projectTimeRange() {
      const start = this.project.startTime || this.project.start_time || this.project.StartTime
      const end = this.project.endTime || this.project.end_time || this.project.EndTime
      if (!start && !end) return '-'
      const formatDate = (d) => {
        if (!d) return ''
        const date = new Date(d)
        return date.toLocaleDateString('zh-CN')
      }
      return `${formatDate(start)} 至 ${formatDate(end)}`
    },

    // 项目开始时间（用于进度卡片）
    projectStartTime() {
      const start = this.project.startTime || this.project.start_time || this.project.StartTime
      if (!start) return '-'
      return new Date(start).toLocaleDateString('zh-CN')
    },

    // 项目截止时间（用于进度卡片）
    projectEndTime() {
      const end = this.project.endTime || this.project.end_time || this.project.EndTime
      if (!end) return '-'
      return new Date(end).toLocaleDateString('zh-CN')
    },

    // 成员统计
    memberStats() {
      const stats = { admin: 0, member: 0, viewer: 0 }
      this.members.forEach(m => {
        const role = (m.Role || m.role || '').toLowerCase()
        if (role === 'admin') stats.admin++
        else if (role === 'member') stats.member++
        else stats.viewer++
      })
      return stats
    },

    // 显示的成员头像（最多5个）
    displayMembers() {
      return this.members.slice(0, 5)
    },

    // 经销商统计（基于波次汇总）
    dealerStats() {
      let total = 0
      let completed = 0
      this.waves.forEach(w => {
        total += w.dealerTotal || 0
        completed += w.completedCount || 0
      })
      return { total, completed }
    },

    // 有审核数据的波次（reviewTotal > 0 的波次）
    reviewWaves() {
      return this.waves.filter(w => w.reviewTotal > 0)
    },

    // 超期风险数据（基于经销商下发统计）
    riskData() {
      return this.calculateOverdueRisk(
        this.project,
        this.dealerStats.total,
        this.dealerStats.completed
      )
    },

    // 进度条颜色
    progressColor() {
      if (this.riskData.overdueRisk === 'high') return '#f56c6c'
      if (this.riskData.overdueRisk === 'medium') return '#e6a23c'
      return '#67c23a'
    },

    // 风险图标
    riskIcon() {
      const map = {
        low: 'el-icon-success',
        medium: 'el-icon-warning',
        high: 'el-icon-error',
        expired: 'el-icon-remove',
        completed: 'el-icon-success'
      }
      return map[this.riskData.overdueRisk] || 'el-icon-info'
    },

    // 风险标签
    riskLabel() {
      const map = {
        low: '进度正常',
        medium: '进度略滞后',
        high: '进度严重滞后',
        expired: '已超期',
        completed: '已完成'
      }
      return map[this.riskData.overdueRisk] || '未知'
    }
  },

  mounted() {
    this.loadData()
  },

  watch: {
    projectId: {
      immediate: false,
      handler() {
        this.loadData()
      }
    }
  },

  methods: {
    async loadData() {
      if (!this.projectId) return
      this.loading = true

      try {
        // 并行加载所有数据
        const [projectRes, quotaRes, membersRes, wavesRes] = await Promise.all([
          getProjectDetail(this.projectId).catch(e => { console.error('getProjectDetail error:', e); return {} }),
          getProjectQuota(this.projectId).catch(() => ({})),
          getProjectMembers({ ProjectId: parseInt(this.projectId) }).catch(() => ({})),
          getWaveList(this.projectId).catch(() => ({}))
        ])

        // 处理项目信息
        // 后端返回结构: { success, errcode, message, data: ProjectOutput }
        const projectData = projectRes?.data?.data || projectRes?.data || projectRes || {}
        this.project = projectData

        // 处理配额数据
        const quotaList = quotaRes?.data?.data || quotaRes?.data || []
        this.quotaList = quotaList
        this.quota = {
          total: quotaList.reduce((sum, q) => sum + (q.target || 0), 0),
          completed: quotaList.reduce((sum, q) => sum + (q.completed || 0), 0),
          pending: 0,
          rate: 0
        }
        this.quota.pending = this.quota.total - this.quota.completed
        this.quota.rate = this.quota.total > 0 
          ? Math.round(this.quota.completed / this.quota.total * 100) 
          : 0

        // 处理成员数据
        this.members = membersRes?.data?.data || membersRes?.data || []

        // 处理波次数据
        const wavesData = wavesRes?.data?.data || wavesRes?.data || {}
        const waveRows = wavesData?.rows || wavesData?.Rows || []
        this.waves = waveRows.map(w => {
          const today = new Date()
          const startTime = w.StartTime || w.startTime
          const endTime = w.EndTime || w.endTime
          const endDate = endTime ? new Date(endTime) : null
          const dealerTotal = w.DealerCount || w.dealerCount || 0
          // 使用后端返回的已完成数量
          const completedCount = w.CompletedCount || w.completedCount || 0
          // 审核统计字段
          const reviewTotal = w.ReviewTotal || w.reviewTotal || 0
          const reviewPending = w.ReviewPending || w.reviewPending || 0
          const review1 = w.Review1 || w.review1 || 0
          const review2 = w.Review2 || w.review2 || 0
          const review3 = w.Review3 || w.review3 || 0
          const reviewCompleted = w.ReviewCompleted || w.reviewCompleted || 0
          
          return {
            id: w.Id || w.id,
            name: w.Name || w.name,
            formKeys: w.FormKeys || w.formKeys || [],
            startTime: startTime,
            endTime: endTime,
            dealerTotal: dealerTotal,
            completedCount: completedCount,
            percentage: dealerTotal > 0 ? Math.round(completedCount / dealerTotal * 100) : 0,
            isOverdue: endDate && today > endDate && completedCount < dealerTotal,
            remainingDays: endDate ? Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24))) : null,
            // 审核进度
            reviewTotal: reviewTotal,
            reviewPending: reviewPending,
            review1: review1,
            review2: review2,
            review3: review3,
            reviewCompleted: reviewCompleted,
            reviewPercentage: reviewTotal > 0 ? Math.round(reviewCompleted / reviewTotal * 100) : 0
          }
        })

      } catch (e) {
        console.error('加载概览数据失败', e)
      } finally {
        this.loading = false
      }
    },

    // 计算超期风险
    calculateOverdueRisk(project, totalQuota, completedQuota) {
      const today = new Date()
      // 支持 snake_case 和 camelCase 两种命名
      const startTimeStr = project.start_time || project.startTime
      const endTimeStr = project.end_time || project.endTime
      const targetQuota = project.target_quota || project.targetQuota
      
      // 如果项目设置了目标配额，优先使用
      const effectiveTotal = targetQuota || totalQuota
      
      const startDate = startTimeStr ? new Date(startTimeStr) : new Date()
      const endDate = endTimeStr ? new Date(endTimeStr) : new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)

      const totalDays = Math.max(1, (endDate - startDate) / (1000 * 60 * 60 * 24))
      const elapsedDays = Math.max(1, (today - startDate) / (1000 * 60 * 60 * 24))
      const remainingDays = Math.max(0, Math.round((endDate - today) / (1000 * 60 * 60 * 24)))
      const remainingQuota = effectiveTotal - completedQuota

      const dailyAverage = completedQuota / elapsedDays
      const requiredDailyAverage = remainingDays > 0 ? remainingQuota / remainingDays : Infinity

      let predictedDaysToFinish = Infinity
      if (dailyAverage > 0) {
        predictedDaysToFinish = remainingQuota / dailyAverage
      }

      let overdueRate = 0
      let overdueRisk = 'low'

      if (remainingDays <= 0) {
        overdueRate = completedQuota >= effectiveTotal ? 0 : 100
        overdueRisk = completedQuota >= effectiveTotal ? 'completed' : 'expired'
      } else if (dailyAverage <= 0 && remainingQuota > 0) {
        overdueRate = 100
        overdueRisk = 'high'
      } else if (effectiveTotal > 0) {
        const gap = predictedDaysToFinish - remainingDays
        if (gap <= 0) {
          overdueRate = 0
          overdueRisk = 'low'
        } else {
          overdueRate = Math.min(100, Math.round((gap / remainingDays) * 100))
          if (overdueRate >= 50) overdueRisk = 'high'
          else if (overdueRate >= 20) overdueRisk = 'medium'
          else overdueRisk = 'low'
        }
      }

      const speedUpRatio = dailyAverage > 0 ? Math.round(requiredDailyAverage / dailyAverage * 10) / 10 : Infinity

      return {
        totalDays: Math.round(totalDays),
        elapsedDays: Math.round(elapsedDays),
        remainingDays,
        completionRate: effectiveTotal > 0 ? Math.round(completedQuota / effectiveTotal * 100) : 0,
        dailyAverage: Math.round(dailyAverage * 10) / 10,
        requiredDailyAverage: Math.round(requiredDailyAverage * 10) / 10,
        overdueRate,
        overdueRisk,
        speedUpRatio,
        needSpeedUp: speedUpRatio > 1
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('zh-CN')
    },

    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]+>/g, '')
    },

    getInitial(name) {
      if (!name) return '?'
      return name.charAt(0).toUpperCase()
    }
  }
}
</script>

<style lang="scss" scoped>
.project-overview {
  padding: 0;
}

.row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  &.full-width {
    flex: none;
    width: 100%;
  }
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
  font-weight: 500;
  color: #303133;

  i {
    font-size: 18px;
    color: #409eff;
  }

  .count-badge {
    margin-left: auto;
    padding: 2px 8px;
    background: #f0f0f0;
    border-radius: 10px;
    font-size: 12px;
    color: #909399;
  }
}

.card-body {
  padding: 20px;
}

/* 项目信息卡片 */
.project-info-card .info-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    width: 80px;
    color: #909399;
    font-size: 13px;
  }

  .value {
    flex: 1;
    color: #303133;
    font-size: 14px;
  }
}

/* 项目进度卡片 */
.project-progress-card {
  .progress-main {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    .progress-percent {
      font-size: 28px;
      font-weight: 600;
      color: #303133;
      min-width: 70px;
    }

    .el-progress {
      flex: 1;
    }
  }

  .time-info {
    display: flex;
    gap: 24px;
    margin-bottom: 16px;

    .time-item {
      label {
        display: block;
        color: #909399;
        font-size: 12px;
        margin-bottom: 4px;
      }

      span {
        color: #303133;
        font-size: 14px;
      }
    }
  }

  .risk-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 13px;

    &.risk-low {
      background: #f0f9eb;
      color: #67c23a;
    }

    &.risk-medium {
      background: #fdf6ec;
      color: #e6a23c;
    }

    &.risk-high, &.risk-expired {
      background: #fef0f0;
      color: #f56c6c;
    }

    &.risk-completed {
      background: #ecf5ff;
      color: #409eff;
    }

    .risk-label {
      font-weight: 500;
    }

    .risk-value {
      margin-left: auto;
      opacity: 0.8;
    }

    .risk-hint {
      opacity: 0.7;
    }
  }
}

/* 配额完成度卡片 */
.quota-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  .summary-item {
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    color: #fff;

    .item-value {
      font-size: 28px;
      font-weight: 600;
      margin-bottom: 6px;
    }

    .item-label {
      font-size: 13px;
      opacity: 0.9;
    }
  }
}

/* 成员卡片 */
.members-card {
  .role-stats {
    display: flex;
    gap: 24px;
    margin-bottom: 16px;

    .role-item {
      text-align: center;

      .role-count {
        display: block;
        font-size: 24px;
        font-weight: 600;
        color: #303133;
      }

      .role-label {
        font-size: 12px;
        color: #909399;
      }
    }
  }

  .member-avatars {
    display: flex;
    gap: 8px;

    .avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 500;

      &.more {
        background: #e4e7ed;
        color: #909399;
        font-size: 12px;
      }
    }
  }
}

/* 波次回收进度卡片 */
.forms-progress-card {
  .form-progress-list {
    max-height: 300px;
    overflow-y: overlay;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 3px;
    }
    &:hover::-webkit-scrollbar-thumb {
      background: #c0c4cc;
    }
  }

  .form-item {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }

    .wave-info {
      min-width: 160px;
      max-width: 200px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .wave-name {
        font-size: 13px;
        color: #303133;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .wave-forms {
        display: flex;
        gap: 4px;
      }
    }

    .form-name {
      width: 120px;
      font-size: 13px;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .form-progress {
      flex: 1;
    }

    .form-stats {
      width: 70px;
      text-align: right;
      font-size: 12px;
      color: #909399;
    }
  }

  .empty-tip {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 20px 0;
  }
}

.text-danger {
  color: #f56c6c !important;
}

/* 数据审核进度卡片（复用波次回收进度的样式） */
.review-progress-card {
  .review-waves-list {
    max-height: 400px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 3px;
    }
    &:hover::-webkit-scrollbar-thumb {
      background: #c0c4cc;
    }
  }

  .review-wave-block {
    padding: 16px;
    background: #fafafa;
    border-radius: 8px;
    margin-bottom: 12px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .wave-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .wave-name {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
    
    .wave-total {
      font-size: 12px;
      color: #909399;
    }
  }

  .review-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .review-item {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .review-label {
      width: 60px;
      font-size: 13px;
      color: #606266;
    }
    
    .review-progress {
      flex: 1;
    }
    
    .review-stats {
      width: 70px;
      text-align: right;
      font-size: 12px;
      color: #909399;
    }
  }

  .empty-tip {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 20px 0;
  }
}
</style>
